import { type MessageKey } from "@/composables/useI18n";
import { db } from "@/firebase";
import { i18n } from "@/i18n"; // not `@/main` — that import cycles and throws on Vite HMR
import { useUserStore } from "@/store/user";
import type { Account } from "@aircap/common/account";
import type { AuthRole } from "@aircap/common/auth";
import {
  accountMemberManagerRoles,
  accountMemberOnboardingRoles,
  accountViewerRoles,
  allRoles,
  apiKeyManagerRoles,
  supervisorRoles,
} from "@aircap/common/auth";
import { getAuth } from "firebase/auth";
import { doc, getDoc, getDocFromCache } from "firebase/firestore";
import "vue-router";
import { createRouter, createWebHistory } from "vue-router";

declare module "vue-router" {
  interface RouteMeta {
    title: MessageKey | null | undefined;
    roles: readonly AuthRole[] | false | undefined; // allowed roles for this route, empty = admin only, false = public
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      redirect: { name: "Login" },
      component: () => import("@/layouts/SideMenu.vue"),
      children: [
        {
          path: "/accounts",
          name: "Accounts",
          component: () => import("@/views/Accounts/Accounts.vue"),
          meta: { title: "accounts.title", roles: accountViewerRoles },
        },
        {
          path: "/openAccount",
          alias: "/new",
          name: "OpenAccount",
          component: () => import("@/views/Onboarding/OpenAccount.vue"),
          meta: { title: "onboarding.title", roles: ["user.accountHolder"] },
        },
        {
          path: "/dashboard",
          name: "SupervisorOrgDashboard",
          component: () => import("@/views/SupervisorOrgDashboard/SupervisorOrgDashboard.vue"),
          meta: { title: "supervisorOrgDashboard.title", roles: supervisorRoles },
        },
        {
          path: "/account",
          name: "MyAccount",
          component: () => import("@/views/MyAccount/MyAccount.vue"),
          meta: { title: "myAccount.title", roles: allRoles },
        },
        {
          path: "/settings",
          name: "SupervisorOrgSettings",
          redirect: { name: "SupervisorOrgGeneralSettings" },
          component: () => import("@/views/SupervisorOrgSettings/SupervisorOrgSettings.vue"),
          children: [
            {
              path: "general",
              name: "SupervisorOrgGeneralSettings",
              component: () => import("@/views/SupervisorOrgSettings/views/General.vue"),
              meta: { title: "supervisorOrgSettings.title", roles: supervisorRoles },
            },
            {
              path: "users",
              name: "SupervisorOrgUsers",
              component: () => import("@/views/SupervisorOrgSettings/views/Users.vue"),
              meta: { title: "supervisorOrgSettings.title", roles: supervisorRoles },
            },
            {
              path: "admin",
              name: "SupervisorOrgAdmin",
              component: () => import("@/views/SupervisorOrgSettings/views/Admin.vue"),
              meta: { title: "supervisorOrgSettings.title", roles: [] }, // only admin
            },
            {
              path: "api",
              name: "SupervisorOrgApiSettings",
              component: () => import("@/views/SupervisorOrgSettings/views/API.vue"),
              meta: { title: "supervisorOrgSettings.title", roles: apiKeyManagerRoles },
            },
          ],
        },
        {
          path: "account/:accountId",
          name: "Account",
          component: () => import("@/views/Account/Account.vue"),
          meta: { title: null, roles: accountViewerRoles },
          props: true,
          children: [
            {
              path: "transactions",
              name: "Transactions",
              component: () => import("@/views/Account/views/Transactions/TransactionsTable.vue"),
              meta: { title: null, roles: accountViewerRoles },
              props: true,
            },
            {
              path: "opening",
              name: "Opening",
              component: () => import("@/views/Account/views/Opening/Opening.vue"),
              meta: { title: null, roles: accountViewerRoles },
              props: true,
            },
            {
              path: "member-onboarding",
              name: "MemberOnboarding",
              component: () =>
                import("@/views/Account/views/MemberOnboarding/MemberOnboarding.vue"),
              meta: { title: null, roles: accountMemberOnboardingRoles },
              props: true,
            },
            {
              path: "dashboard",
              name: "Dashboard",
              component: () => import("@/views/Account/views/Dashboard/Dashboard.vue"),
              meta: { title: null, roles: accountViewerRoles },
              props: true,
            },
            {
              path: "notifications",
              name: "Notifications",
              component: () => import("@/views/Account/views/Notifications.vue"),
              meta: { title: null, roles: supervisorRoles },
              props: true,
            },
            {
              path: "settings",
              name: "Settings",
              component: () => import("@/views/Account/views/Settings/Settings.vue"),
              meta: { title: null, roles: supervisorRoles },
              props: true,
            },
            {
              path: "transfer-requests",
              alias: "transfers", // previous path, keeps old links working
              name: "TransferRequests",
              component: () => import("@/views/Account/views/TransferRequests.vue"),
              meta: { title: null, roles: accountViewerRoles },
              props: true,
            },
            {
              path: "beneficiaries",
              name: "Beneficiaries",
              component: () => import("@/views/Account/views/Beneficiaries.vue"),
              meta: { title: null, roles: accountViewerRoles },
              props: true,
            },
            {
              path: "members",
              name: "Members",
              component: () => import("@/views/Account/views/Members.vue"),
              meta: { title: null, roles: accountMemberManagerRoles },
              props: true,
            },
            {
              path: "budget",
              name: "Budget",
              component: () => import("@/views/Account/views/Budget/Budget.vue"),
              meta: { title: null, roles: accountViewerRoles },
              props: true,
            },
            {
              path: "commercialisation",
              name: "Commercialisation",
              component: () =>
                import("@/views/Account/views/Commercialisation/Commercialisation.vue"),
              meta: { title: null, roles: accountViewerRoles },
              props: true,
            },
            {
              path: "logs",
              name: "Logs",
              component: () => import("@/views/Account/views/Logs/Logs.vue"),
              meta: { title: null, roles: [] }, // only admin
              props: true,
            },
          ],
        },
        {
          path: "account/:accountId/beneficiary/:beneficiaryIban",
          name: "BeneficiaryDetail",
          redirect: { name: "BeneficiaryTransactions" },
          component: () => import("@/views/Account/views/BeneficiaryDetail/BeneficiaryDetail.vue"),
          props: true,
          children: [
            {
              path: "transactions",
              name: "BeneficiaryTransactions",
              component: () => import("@/views/Account/views/Transactions/TransactionsTable.vue"),
              meta: { title: null, roles: accountViewerRoles },
              props: true,
            },
            {
              path: "transfer-requests",
              alias: "pending-transactions", // previous path, keeps old links working
              name: "BeneficiaryTransferRequest",
              component: () =>
                import("@/views/Account/views/BeneficiaryDetail/TransferRequests.vue"),
              meta: { title: null, roles: accountViewerRoles },
              props: true,
            },
          ],
        },
      ],
    },
    // Previous paths, keep links already sent working
    { path: "/projects", redirect: { name: "Accounts" } },
    {
      path: "/project/:rest(.*)+",
      redirect: (to) => ({ path: to.path.replace(/^\/project\//, "/account/") }),
    },
    {
      path: "/link",
      name: "LinkStart",
      meta: { title: "link.title", roles: false }, // public
      props: true,
      component: () => import("@/views/Link/Start.vue"),
    },
    {
      path: "/link/stop-notification",
      name: "LinkStopNotification",
      meta: { title: "link.stop-notification-title", roles: false }, // public
      props: true,
      component: () => import("@/views/Link/StopNotification.vue"),
    },
    {
      path: "/link/bridge/return",
      name: "LinkBridgeReturn",
      meta: { title: "link.title", roles: false }, // public
      props: true,
      component: () => import("@/views/Link/BridgeReturn.vue"),
    },
    {
      path: "/link/powens/return",
      name: "LinkPowensReturn",
      meta: { title: "link.title", roles: false }, // public
      props: true,
      component: () => import("@/views/Link/PowensReturn.vue"),
    },
    {
      path: "/link/gcl/return",
      name: "LinkGCLReturn",
      meta: { title: "link.title", roles: false }, // public
      props: true,
      component: () => import("@/views/Link/GCLReturn.vue"),
    },
    {
      path: "/open/:action",
      name: "Open",
      meta: { title: "open.title", roles: allRoles },
      component: () => import("@/views/Open.vue"),
    },
    {
      path: "/liveness/return",
      name: "LivenessReturn",
      meta: { title: "liveness.title", roles: false }, // public
      component: () => import("@/views/Liveness/Return.vue"),
    },
    {
      path: "/login",
      name: "Login",
      meta: { title: "login.title", roles: false }, // public
      component: () => import("@/views/Login.vue"),
    },
    {
      path: "/signup",
      name: "Signup",
      meta: { title: "signup.title", roles: false }, // public
      component: () => import("@/views/Signup.vue"),
    },
    {
      // Temporary path until the Treezor system is ready; rename once account opening lives there.
      path: "/openTreezorAccount",
      name: "Join",
      meta: { title: "join.title", roles: false }, // public
      component: () => import("@/views/Join.vue"),
    },
    {
      path: "/mfa",
      name: "Mfa",
      meta: { title: "mfa.title", roles: accountViewerRoles },
      component: () => import("@/views/Mfa.vue"),
    },
    {
      path: "/admin",
      name: "Admin",
      redirect: { name: "AdminSupervisorOrgs" },
      component: () => import("@/views/Admin/AdminLayout.vue"),
      children: [
        {
          path: "clients",
          redirect: { name: "AdminSupervisorOrgs" }, // previous path, keeps old links working
        },
        {
          path: "supervisor-orgs",
          name: "AdminSupervisorOrgs",
          component: () => import("@/views/Admin/AdminSupervisorOrgs.vue"),
          meta: { title: "sidemenu.supervisorOrgs", roles: [] }, // only admin
        },
        {
          path: "projects",
          redirect: { name: "AdminAccounts" }, // previous path, keeps old links working
        },
        {
          path: "accounts",
          name: "AdminAccounts",
          component: () => import("@/views/Admin/AdminAccounts.vue"),
          meta: { title: "sidemenu.accounts", roles: [] }, // only admin
        },
        {
          path: "admins",
          name: "AdminAdmins",
          component: () => import("@/views/Admin/AdminAdmins.vue"),
          meta: { title: "adminAdmins.title", roles: [] }, // only admin
        },
      ],
    },
  ],
});

router.beforeEach(async (to) => {
  if (!to.name) {
    console.warn("Unknown route", { route: to });
    return { name: "Login" };
  }

  // Ensure Firebase Auth has finished hydrating from persistence before role checks.
  await getAuth().authStateReady();

  const user = useUserStore();
  await user.waitForLoading();

  // Backend admin links carry ?supervisorOrgId (?clientId in links sent before the rename)
  const supervisorOrgId = to.query.supervisorOrgId ?? to.query.clientId;
  if (user.isKind("admin") && supervisorOrgId && typeof supervisorOrgId === "string") {
    console.log("Setting supervisorOrgId from query parameter:", supervisorOrgId);
    user.setAdminSupervisorOrgId(supervisorOrgId);
  }

  if (to.meta.roles === false) {
    console.log(`Moving to public route ${to.name.toString()}`);
    return true;
  }

  if (!user.authenticatedUser) {
    console.warn(
      `Route ${to.name.toString()} requires authentication but user is not authenticated, redirecting to login`,
      { route: to },
    );
    return { name: "Login", query: { redirect: to.fullPath } };
  }

  if (!user.authenticatedUser.authClaims.isMfaEnrolled) {
    console.warn("User does not have MFA enrolled, redirecting to MFA setup");
    return to.name === "Mfa" || { name: "Mfa" };
  }

  if (!user.authenticatedUser.authClaims.loggedInWithMfa) {
    console.warn("User did not logged in with MFA, redirecting to Login");
    return { name: "Login", query: { redirect: to.fullPath } };
  }

  const roles = to.meta.roles || [];

  if (typeof to.params.accountId === "string") {
    const accountRef = doc<Account>(db, "accounts", to.params.accountId);
    const account = await getDocFromCache(accountRef)
      .catch(() => getDoc(accountRef))
      .then((snapshot) => snapshot.data())
      .catch(() => undefined);
    if (user.hasRole(roles, { account: account && { ...account, id: to.params.accountId } }))
      return true;
    if (account?.users?.[user.authenticatedUser.firebaseUser.uid]?.role === "pendingAccountHolder")
      return { name: "MemberOnboarding", params: { accountId: to.params.accountId } };

    console.warn("User does not have the required role on this account", {
      route: to,
      requiredRoles: to.meta.roles,
    });
    return { path: "/" };
  }

  // Without an account, holders and operators have no role yet: let them in when the route lists
  // a role of their kind.
  let canAccessRoute;
  switch (user.authenticatedUser.authClaims.kind) {
    case "admin":
    case "supervisor":
      canAccessRoute = user.hasRole(roles, "supervisorOrg");
      break;
    case "operator":
      canAccessRoute = roles.includes("operator");
      break;
    case "user":
      canAccessRoute = roles.some((role) => role.startsWith("user."));
      break;
  }
  if (canAccessRoute) {
    console.log(
      `Moving to route ${to.name.toString()} that requires roles ${to.meta.roles?.join(", ") ?? "unknown"}`,
    );
    return true;
  }

  console.warn("User does not have the required role to access this route", {
    route: to,
    requiredRoles: to.meta.roles,
    user,
  });
  void getAuth().signOut();
  return false;
});

router.beforeResolve((to) => {
  if (!to.meta.title) return;
  const { t } = i18n.global;
  document.title = [t(to.meta.title), "Aircap"].filter((x) => x).join(" - ");
});

export default router;
